/**
 * 愛知県の料理12品。
 *
 * 豆味噌の県なので、放っておくと赤黒い料理ばかりになる。
 * 味噌を使うものは田楽・味噌カツ・味噌煮込みうどんの3つにとどめ、
 * 残りは果物・菓子・漬物・干物・押し寿司で色と形を散らしている。
 *
 * 他県と重ならないように外したもの。
 *  - ういろう … 山口が「外郎」で公開済み。見た目がほぼ同じなので外した
 *  - 五平餅 … 岐阜に割り当てた
 *  - 餃子 … 静岡（浜松餃子）・福岡（鉄鍋餃子）で公開済み
 *  - きしめん … 味噌煮込みうどんと麺が二重になるので外した
 *  - たこ飯 … 岡山の「たこめし」と重なる
 *  - ふな味噌 … 岐阜の西濃にも同じものがある。どちらにも入れないことにした
 *  - 和牛・ラーメン・そうめん … すでに他県に多い
 *
 * うなぎは静岡が「うなぎの蒲焼き」で公開済みだが、ひつまぶしは
 * 刻んだ身を丸いおひつに敷きつめる形で、長い一枚身とは絵が別になる。
 * 静岡と並べても見分けがつくよう、仕様に「刻んである」「丸いおひつ」を明記した。
 *
 * いちじくの「日本一」は限定語ごと引用する。
 * 愛知県公式は栽培面積・産出額が全国1位、収穫量は全国2位と書いている。
 * 「収穫量日本一」は誤りなので書かない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：いちじく・いがまんじゅう・鬼まんじゅう・おしもん・かりもりの粕漬け・もろこずし（店で食べられる品に差し替え）
 * - 天むす：発祥の店名と創作の年代（昭和30年代前半）は、なごやめし普及促進協議会のページにあるが、特定の店名を出さない方針と「発祥の店」は書かない方針により書かない
 * - 天むす：協議会ページにある「海苔を斜めに巻くのは美智子様のストールの巻き方にあやかった」という話は、人物にかかわる逸話で、かつ表記（「現皇后」）が現在と合わないため書かない
 * - 鉄板ナポリタン：考案した喫茶店の名は出典にもなく、店名を書かない方針でもあるため書かない
 * - 鉄板ナポリタン：出典にある「平成以降の喫茶店減少で廃れかけ、近年のナポリタンブームで復権」は時期で変わる話なので書かない
 * - きしめん：名前の由来・発祥は、農水省が「刈谷市のひもかわがルーツとされる」「碁石麺から」と書き、協議会は雉肉入り説・紀州伝来説・碁石型説・ひもかわ説など複数の説を併記している。割れているので書かない
 * - きしめん：「名古屋城築城の際に手早く調理できるよう麺を薄くした」は協議会ページで「伝説」とされているので書かない
 * - どて煮：発祥の時期や店は出典で「不明」とされているので書かない（明治以降に広まったという推測も書かない）
 * - 台湾ラーメン：始まり（昭和40年代ごろ、名古屋市内の台湾料理店の創業者がまかないとして作った）は出典に店名つきで書かれているが、特定の店名を出さない方針と「発祥の店」を書かない方針により書かない
 * - あんかけスパ：考案者（店の創業者）と、呼び名を広めた店の名は出典にあるが、店名を出さない方針と「発祥の店」を書かない方針により書かない
 * - あんかけスパ：フランチャイズ展開（平成27年）の話は企業の動きで変わりうるので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { aichiSceneryTour } from './aichiScenes';
import { AICHI_FOOD_HULLS } from './aichiFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kushi-asari',
    level: 1,
    name: '串あさり',
    shortName: '串あさり',
    image: '/assets/foods/aichi/01_kushi_asari.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '三粒だけ刺して、日に干す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kushiasari_aichi.html',
    icon: { label: '串あさり', shape: 'roe', baseColor: '#c8934a', accentColor: '#5a3a28' },
    reward: {
      unlockScore: 200,
      area: '西尾市・蒲郡市',
      headline: '殻から外したアサリの身を竹串に三粒だけ刺し、日なたで六、七時間干した三河の保存食。',
      tip: '江戸時代には徳川幕府への献上品で、東海道の宿場でも出されていた。愛知県はアサリの漁獲量が全国1位で、西尾市と蒲郡市が特に多い。半田市亀崎の潮干祭には欠かせない一品で、正月の膳でも親しまれてきた。',
      links: searchLinks('西尾市', '串あさり'),
    },
  },
  {
    id: 'tenmusu',
    level: 2,
    name: '天むす',
    shortName: '天むす',
    image: '/assets/foods/aichi/02_tenmusu.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '小えびの天ぷらを具にして海苔を巻いた、小ぶりのおむすび。ふつうのおむすびの半分ほどの大きさで、えび天のうまみとご飯のほのかな塩気が合う。三重県津市で生まれ、名古屋で広く知られるようになった。',
    source:
      'https://nagoya-meshi.jp/cms/item/tenmusu/?no=7',
    icon: { label: '天むす', shape: 'mochi', baseColor: '#f4f1e8', accentColor: '#e0893a' },
    reward: {
      unlockScore: 500,
      area: '名古屋市',
      headline: 'えび天を包んだ一口おむすび',
      tip: '天ぷら屋の女主人が夫のために作ったまかないが始まりとされる。芸能人の楽屋への差し入れで人気が出て、名古屋名物として知られるようになった。',
      links: searchLinks('名古屋市', '天むす'),
      official: [{ label: 'なごやめし普及促進協議会「天むす」', url: 'https://nagoya-meshi.jp/cms/item/tenmusu/?no=7' }, { label: '愛知県観光コンベンション局「こってり愛知de首都圏」', url: 'https://aichinow.pref.aichi.jp/kotteri/gourmet/kotteri.html' }],
    },
  },
  {
    id: 'teppan-napolitan',
    level: 3,
    name: '鉄板ナポリタン',
    shortName: '鉄板ナポリタン',
    image: '/assets/foods/aichi/03_teppan_napolitan.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '熱した鉄板皿にケチャップで味付けたスパゲティを盛り、まわりに溶き卵を流し入れる。具は赤いウインナー、玉ねぎ、ピーマンなど。スパゲティの専門店ではなく、おもに名古屋の喫茶店で食べられてきた。',
    source:
      'https://nagoya-meshi.jp/cms/item/teppanspa/?no=9',
    icon: { label: 'ナポリタン', shape: 'curry-plate', baseColor: '#d9502e', accentColor: '#f2d04b' },
    reward: {
      unlockScore: 1000,
      area: '名古屋市',
      headline: '鉄板の上で卵と食べるナポリタン',
      tip: '昭和30年代後半、イタリアで食べたパスタが途中で冷めることを気にした名古屋の喫茶店主が、鉄板皿を使うことを思いついたとされる。',
      links: searchLinks('名古屋市', '鉄板ナポリタン'),
      official: [{ label: 'なごやめし普及促進協議会「鉄板スパ」', url: 'https://nagoya-meshi.jp/cms/item/teppanspa/?no=9' }],
    },
  },
  {
    id: 'kishimen',
    level: 4,
    name: 'きしめん',
    shortName: 'きしめん',
    image: '/assets/foods/aichi/04_kishimen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '厚さ1mm、幅7〜8mmほどの平たいうどん。たまり醤油を使い、ムロアジなどでだしをとったつゆに、油揚げ・かまぼこ・青物・花かつおをのせるのが基本。平らな麺につゆがよくからむ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kishimen_aichi.html',
    icon: { label: 'きしめん', shape: 'noodle-bowl', baseColor: '#efe6cf', accentColor: '#a8743a' },
    reward: {
      unlockScore: 1800,
      area: '名古屋市ほか愛知県',
      headline: '幅の広い薄い平打ち麺',
      tip: '1年を通して日常的に食べられ、夏には冷たい「ころ」も食べる。名古屋特有の赤いかまぼこを添えるのが郷土らしい形とされる。',
      links: searchLinks('名古屋市', 'きしめん'),
      official: [{ label: '農林水産省「うちの郷土料理」きしめん', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kishimen_aichi.html' }, { label: 'なごやめし普及促進協議会「きしめん」', url: 'https://nagoya-meshi.jp/cms/item/kishimen/?no=5' }],
    },
  },
  {
    id: 'dote-ni',
    level: 5,
    name: 'どて煮',
    shortName: 'どて煮',
    image: '/assets/foods/aichi/05_dote_ni.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '豚や牛の内臓を豆味噌で煮込む。こんにゃくや大根を一緒に煮ることも多い。ほかの地域の醤油だしの「もつ煮」と違い、豆味噌の濃い甘辛さが特徴。鍋のまわりに味噌を土手のように盛って焦がしながら煮たのが名の由来と言われる。',
    source:
      'https://nagoya-meshi.jp/cms/item/doteni/?no=8',
    icon: { label: 'どて煮', shape: 'pot', baseColor: '#5a2a1c', accentColor: '#6fb24a' },
    reward: {
      unlockScore: 3000,
      area: '名古屋市ほか東海地方',
      headline: '豆味噌で煮込んだもつ',
      tip: 'ねぎや七味唐辛子を薬味にする。ご飯にかけた「どて飯」も名古屋名物のひとつで、どて煮の汁を串カツにかける食べ方もある。',
      links: searchLinks('名古屋市', 'どて煮'),
      official: [{ label: 'なごやめし普及促進協議会「どて煮」', url: 'https://nagoya-meshi.jp/cms/item/doteni/?no=8' }],
    },
  },
  {
    id: 'taiwan-ramen',
    level: 6,
    name: '台湾ラーメン',
    shortName: '台湾ラーメン',
    image: '/assets/foods/aichi/06_taiwan_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '台湾の担仔麺（タンツーメン）を激辛にした、名古屋で生まれたラーメン。唐辛子とにんにくで炒めた辛いひき肉をのせ、具はほかにニラともやしが基本。スープは鶏がらベースですっきりしている。台湾にはない料理。',
    source:
      'https://nagoya-meshi.jp/cms/item/taiwanramen/?no=10',
    icon: { label: '台湾ラーメン', shape: 'ramen', baseColor: '#c0392b', accentColor: '#4f9a3a' },
    reward: {
      unlockScore: 4500,
      area: '名古屋市',
      headline: '台湾にはない名古屋の激辛麺',
      tip: '昭和50年代の激辛ブームで広まり、名古屋市・愛知県内の中華料理店やラーメン店の約半数が出しているといわれる。「台湾まぜそば」など派生の品も生まれた。',
      links: searchLinks('名古屋市', '台湾ラーメン'),
      official: [{ label: 'なごやめし普及促進協議会「台湾ラーメン」', url: 'https://nagoya-meshi.jp/cms/item/taiwanramen/?no=10' }],
    },
  },
  {
    id: 'tebasaki',
    level: 7,
    name: '手羽先',
    shortName: '手羽先',
    image: '/assets/foods/aichi/07_tebasaki.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '骨の先まで、たれが光っている。',
    source:
      'https://www.nagoya-info.jp/gourmet/digest/',
    icon: { label: '手羽先', shape: 'fish', baseColor: '#9a5228', accentColor: '#f0e8d8' },
    reward: {
      unlockScore: 6500,
      area: '名古屋市',
      headline: '鶏の手羽先を素揚げして甘辛いたれを塗り、こしょうとごまをふった名古屋の定番。',
      tip: '手羽先は鶏の羽の先の部分で、ゼラチン質と脂肪が多く肉が少ないため、かつては飼料用とされていた。素揚げしてから甘辛いたれを塗り、スパイスとごまをふりかける作り方が広く知られている。',
      links: searchLinks('名古屋市', '手羽先'),
    },
  },
  {
    id: 'miso-dengaku',
    level: 8,
    name: '味噌田楽',
    shortName: '味噌田楽',
    image: '/assets/foods/aichi/08_miso_dengaku.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '白い豆腐に、赤黒い味噌をのせて焼く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/misodengaku_aichi.html',
    icon: { label: '味噌田楽', shape: 'dumpling', baseColor: '#f0ece0', accentColor: '#7a2820' },
    reward: {
      unlockScore: 9000,
      area: '豊橋市・岡崎市',
      headline: '水切りした豆腐を串に刺して両面を焼き、赤味噌のたれを塗って木の芽を添えた三河の一品。',
      tip: '田楽舞いを踊る田楽法師の姿が、白い豆腐に味噌をのせて串で焼いた形に似ていたことから名がついたと伝わる。江戸時代の『豆腐百珍』にも田楽が紹介されている。三河では菜飯田楽、尾張では麩田楽が名物で、木の芽が加わると春の訪れを知らせる料理になる。',
      links: searchLinks('豊橋市', '菜飯田楽'),
    },
  },
  {
    id: 'ankake-spa',
    level: 9,
    name: 'あんかけスパ',
    shortName: 'あんかけスパ',
    image: '/assets/foods/aichi/09_ankake_spa.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '直径2.2mmほどの極太スパゲティをゆでてからラードで炒め、和食や中華の「あん」のようにとろみのある、こしょうのきいたソースをかける。具はハム主体の「ミラネーゼ」と野菜中心の「カントリー」を合わせた「ミラカン」が定番。',
    source:
      'https://nagoya-meshi.jp/cms/item/ankaespa/?no=6',
    icon: { label: 'あんかけスパ', shape: 'curry-plate', baseColor: '#8a5a2b', accentColor: '#e86f7a' },
    reward: {
      unlockScore: 12000,
      area: '名古屋市',
      headline: 'とろみとこしょうの太麺スパ',
      tip: '昭和30年代に生まれ、「あんかけスパ」という呼び方が定着したのは昭和50年代。愛知県内に専門店が多い。',
      links: searchLinks('名古屋市', 'あんかけスパ'),
      official: [{ label: 'なごやめし普及促進協議会「あんかけスパ」', url: 'https://nagoya-meshi.jp/cms/item/ankaespa/?no=6' }],
    },
  },
  {
    id: 'miso-katsu',
    level: 10,
    name: '味噌カツ',
    shortName: '味噌カツ',
    image: '/assets/foods/aichi/10_miso_katsu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '衣の上から、黒いたれがかかる。',
    source:
      'https://www.nagoya-info.jp/gourmet/digest/',
    icon: { label: '味噌カツ', shape: 'curry-plate', baseColor: '#c89858', accentColor: '#5a2018' },
    reward: {
      unlockScore: 16000,
      area: '名古屋市',
      headline: '揚げたてのとんかつに豆味噌のたれをかけた、名古屋を代表する一皿。',
      tip: '豆味噌をベースにしたたれをかけたとんかつで、串カツの形もある。たれのかけ方や濃さは店ごとに異なり、各店が個性を競っている。',
      links: searchLinks('名古屋市', '味噌カツ'),
    },
  },
  {
    id: 'miso-nikomi-udon',
    level: 11,
    name: '味噌煮込みうどん',
    shortName: '味噌煮込みうどん',
    image: '/assets/foods/aichi/11_miso_nikomi_udon.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '土鍋のまま、湯気を立てて出てくる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/misonikomiudon_aichi.html',
    icon: { label: '味噌煮込みうどん', shape: 'hotpot', baseColor: '#6a2a20', accentColor: '#e8c860' },
    reward: {
      unlockScore: 22000,
      area: '名古屋市・一宮市',
      headline: '八丁味噌仕立ての汁でコシの強いうどんを煮込み、卵を落として蓋をした土鍋のままの一杯。',
      tip: 'もとは家庭料理で、明治時代には一宮市の飲食店で出されていたとされ、のちに名古屋市を中心に広がった。土鍋でだしを沸かし、赤味噌と砂糖を加え、鶏肉と油揚げを煮立たせてからうどんを五分ほど煮て、最後に卵を割り入れ蓋をしてひと煮立ちさせる。',
      links: searchLinks('名古屋市', '味噌煮込みうどん'),
    },
  },
  {
    id: 'hitsumabushi',
    level: 12,
    name: 'ひつまぶし',
    shortName: 'ひつまぶし',
    image: '/assets/foods/aichi/12_hitsumabushi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '刻んで、丸い櫃に敷きつめる。',
    source:
      'https://www.nagoya-info.jp/gourmet/digest/',
    icon: { label: 'ひつまぶし', shape: 'rice-bowl', baseColor: '#7a4a20', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 30000,
      area: '名古屋市',
      headline: 'うなぎの蒲焼きを短冊に刻み、熱いごはんとともに丸いおひつに盛った名古屋の食べ方。',
      tip: '一杯目はそのまま、二杯目は薬味を散らし、三杯目はだしや茶をかけてさらさらと食べる。愛知では濃厚なたれを使う。自分で茶碗によそって食べ方を変えていくのが決まりになっている。',
      links: searchLinks('名古屋市', 'ひつまぶし'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const aichiFoodStage: StageDefinition = {
  regionId: 'aichi',
  regionName: '愛知県',
  mode: 'food',
  title: '愛知めしマージ',
  theme: {
    primaryColor: '#8a2a28',
    secondaryColor: '#c8a038',
    accentColor: '#e8c060',
    backgroundColor: '#160f0e',
  },
  /** 背景ツアー「愛知めぐり」。軸は一日の時間。日の出の日出の石門から、日没の渥美半島へ。 */
  scenery: {
    tour: aichiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, AICHI_FOOD_HULLS),
};
